/**
 * WishlistWatchersCard — live "who's eyeing your SKU" panel for sellers.
 *
 * Data source: GET /api/wishlist-watchers/mine
 *   Returns each shop product the seller owns that at least one shopper has
 *   added to their AI Cart (private OR shared), ranked by unique shoppers.
 *
 * Interaction:
 *   · Click a row to reveal the recent watchers list.
 *   · Named watchers with a phone on file expose a "Send nudge" button that
 *     opens an inline composer prefilled with a template pulled from
 *     GET /api/wishlist-watchers/nudge/template and delivered via
 *     POST /api/wishlist-watchers/nudge.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye, Users, Loader2, ChevronDown, Heart, Package,
  MessageCircle, Send, X as XIcon, Phone, Mail, Edit3, IndianRupee,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

interface RecentWatcher {
  user_id: string | null;
  name: string;
  raw_name?: string;
  at: string;
  source: "shared" | "private";
  has_phone?: boolean;
  last_nudged_at?: string | null;
}
interface WatchedSku {
  product_id: string;
  slug?: string;
  title: string;
  unique_shoppers: number;
  shared_carts: number;
  private_carts: number;
  recent_watchers: RecentWatcher[];
  price?: number;
  currency?: string;
  sibascore?: number;
}
interface WatcherPayload {
  items: WatchedSku[];
  total_unique_shoppers: number;
  total_skus_watched: number;
}
interface NudgeTarget {
  productId: string;
  productTitle: string;
  watcher: RecentWatcher;
}

function fmtWhen(iso: string): string {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString([], { day: "numeric", month: "short" });
  } catch {
    return iso;
  }
}

const NudgeComposer = ({ target, onClose }: { target: NudgeTarget; onClose: () => void }) => {
  const [message, setMessage] = useState("");
  const [channel, setChannel] = useState<"whatsapp" | "sms">("whatsapp");
  const qc = useQueryClient();

  // Fetch the seller's default template as soon as the composer opens so
  // the seller has something to tweak instead of a blank slate.
  const { isLoading: tplLoading } = useQuery({
    queryKey: ["nudge-tpl", target.productId, target.watcher.user_id],
    queryFn: async () => {
      const { data } = await api.get("/wishlist-watchers/nudge/template", {
        params: { product_id: target.productId, watcher_name: target.watcher.raw_name || target.watcher.name },
      });
      setMessage((current) => current || data.template || "");
      return data;
    },
    staleTime: Infinity,
  });

  const send = useMutation({
    mutationFn: async () => (await api.post("/wishlist-watchers/nudge", {
      product_id: target.productId,
      watcher_user_id: target.watcher.user_id,
      channel,
      message: message.trim(),
    })).data,
    onSuccess: (data: any) => {
      if (data?.ok) {
        toast.success(`Nudge sent via ${channel === "whatsapp" ? "WhatsApp" : "SMS"} to ${target.watcher.name}`);
        qc.invalidateQueries({ queryKey: ["wishlist-watchers-mine"] });
        onClose();
      } else {
        toast.error(data?.error || "Nudge failed to deliver");
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Nudge failed"),
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      data-testid="watcher-nudge-composer"
      className="mt-2 rounded-xl border border-emerald-200 bg-white p-3 shadow-sm"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-700 flex items-center gap-1">
          <Send className="h-3 w-3" />
          Send nudge to {target.watcher.name}
        </div>
        <button
          onClick={onClose}
          className="text-neutral-400 hover:text-neutral-900"
          data-testid="watcher-nudge-close"
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex gap-1.5 mb-2">
        <button
          data-testid="watcher-nudge-channel-whatsapp"
          onClick={() => setChannel("whatsapp")}
          aria-pressed={channel === "whatsapp"}
          className={`flex-1 text-xs font-semibold px-2 py-1 rounded-lg border transition-colors ${
            channel === "whatsapp"
              ? "bg-emerald-500 text-white border-emerald-500"
              : "bg-white text-neutral-700 border-neutral-200 hover:border-emerald-300"
          }`}
        >
          <MessageCircle className="h-3 w-3 inline mr-1" /> WhatsApp
        </button>
        <button
          data-testid="watcher-nudge-channel-sms"
          onClick={() => setChannel("sms")}
          aria-pressed={channel === "sms"}
          className={`flex-1 text-xs font-semibold px-2 py-1 rounded-lg border transition-colors ${
            channel === "sms"
              ? "bg-sky-500 text-white border-sky-500"
              : "bg-white text-neutral-700 border-neutral-200 hover:border-sky-300"
          }`}
        >
          <Phone className="h-3 w-3 inline mr-1" /> SMS
        </button>
      </div>
      <textarea
        data-testid="watcher-nudge-message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={5}
        placeholder={tplLoading ? "Loading template…" : "Type your nudge…"}
        className="w-full text-xs px-2.5 py-2 rounded-lg border border-neutral-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none font-mono resize-none"
      />
      <div className="mt-1 flex items-center justify-between">
        <span className={`text-[10px] tabular-nums ${message.length > 1400 ? "text-rose-600 font-bold" : "text-neutral-400"}`}>
          {message.length}/1500
        </span>
        <button
          data-testid="watcher-nudge-send"
          onClick={() => send.mutate()}
          disabled={send.isPending || message.trim().length < 4 || message.length > 1500}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
        >
          {send.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          Send nudge
        </button>
      </div>
    </motion.div>
  );
};

export const WishlistWatchersCard = () => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [nudgeTarget, setNudgeTarget] = useState<NudgeTarget | null>(null);
  const [notifyTarget, setNotifyTarget] = useState<WatchedSku | null>(null);
  const [notifyEvent, setNotifyEvent] = useState<"price_drop" | "back_in_stock" | "custom">("price_drop");
  const [notifyPrice, setNotifyPrice] = useState<string>("");
  const [notifyMessage, setNotifyMessage] = useState<string>("");
  const [notifySending, setNotifySending] = useState(false);
  // Inline "price + stock" editor state — one open row at a time.
  const [inventoryEditor, setInventoryEditor] = useState<string | null>(null);
  const [invPrice, setInvPrice] = useState<string>("");
  const [invStock, setInvStock] = useState<string>("");
  const [invSaving, setInvSaving] = useState(false);
  // Bulk-editor state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState<Record<string, { price: string; stock: string }>>({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery<WatcherPayload>({
    queryKey: ["wishlist-watchers-mine"],
    queryFn: async () => (await api.get("/wishlist-watchers/mine")).data,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  async function submitInventory(sku: WatchedSku) {
    const payload: Record<string, unknown> = {};
    if (invPrice.trim()) payload.price = parseFloat(invPrice);
    if (invStock.trim()) payload.stock_status = invStock.trim();
    if (Object.keys(payload).length === 0) {
      toast.info("Change the price or stock status to save.");
      return;
    }
    setInvSaving(true);
    try {
      const { data } = await api.patch(`/wishlist-watchers/inventory/${sku.product_id}`, payload);
      const triggers = data?.waitlist_triggers || [];
      if (triggers.length) {
        const t = triggers[0];
        const eventLabel = t.event === "price_drop" ? "💸 Price drop" : "🎯 Back in stock";
        toast.success(`${eventLabel} · Auto-sent ${t.sent} waitlist email${t.sent === 1 ? "" : "s"}.`);
      } else {
        toast.success("Saved — no waitlist trigger fired.");
      }
      setInventoryEditor(null);
      setInvPrice("");
      setInvStock("");
      qc.invalidateQueries({ queryKey: ["wishlist-watchers-mine"] });
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Couldn't save inventory.");
    } finally {
      setInvSaving(false);
    }
  }

  async function submitBulk() {
    const rows = Object.entries(bulkRows)
      .map(([product_id, v]) => {
        const row: Record<string, unknown> = { product_id };
        if (v.price.trim()) row.price = parseFloat(v.price);
        if (v.stock.trim()) row.stock_status = v.stock.trim();
        return row;
      })
      .filter(r => "price" in r || "stock_status" in r);
    if (rows.length === 0) {
      toast.info("Fill at least one price or stock value.");
      return;
    }
    setBulkSaving(true);
    try {
      const { data } = await api.post("/wishlist-watchers/inventory/bulk", { rows });
      toast.success(`✅ Updated ${data.processed} SKU${data.processed === 1 ? "" : "s"} · ${data.total_emails_sent} waitlist email${data.total_emails_sent === 1 ? "" : "s"} auto-sent.`);
      setBulkOpen(false);
      setBulkRows({});
      qc.invalidateQueries({ queryKey: ["wishlist-watchers-mine"] });
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkSaving(false);
    }
  }

  async function submitNotify() {
    if (!notifyTarget) return;
    setNotifySending(true);
    try {
      const { data } = await api.post("/wishlist-watchers/notify", {
        product_id: notifyTarget.product_id,
        event_type: notifyEvent,
        new_price: notifyPrice ? parseFloat(notifyPrice) : null,
        currency: notifyTarget.currency || "INR",
        message: notifyMessage.trim() || null,
      });
      if (data?.ok) {
        toast.success(`Waitlist email sent to ${data.sent} watcher${data.sent === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped (no email)` : ""}`);
        setNotifyTarget(null);
        setNotifyPrice("");
        setNotifyMessage("");
      } else {
        toast.error("Nothing sent — no watchers with email on file.");
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Notify failed");
    } finally {
      setNotifySending(false);
    }
  }

  const items = data?.items || [];

  return (
    <div
      data-testid="wishlist-watchers-card"
      className="rounded-2xl border border-neutral-200 bg-gradient-to-br from-emerald-50/40 to-white overflow-hidden shadow-sm"
    >
      <div className="px-5 py-4 flex items-start justify-between gap-3 flex-wrap border-b border-neutral-100 bg-white">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg grid place-items-center bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
            <Heart className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-bold text-neutral-900">
              Wishlist watchers
            </div>
            <div className="text-xs text-neutral-500">
              Live count of shoppers who added your SKUs to any AI Cart™. Start conversations before they hit "buy".
            </div>
          </div>
        </div>
        {data && (
          <div className="flex items-center gap-3 text-right">
            <div>
              <div className="text-2xl font-bold tabular-nums text-neutral-900">
                {data.total_unique_shoppers}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
                watchers
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold tabular-nums text-emerald-700">
                {data.total_skus_watched}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
                skus wanted
              </div>
            </div>
            {(data?.items?.length || 0) > 1 && (
              <button
                data-testid="wishlist-bulk-open"
                onClick={() => { setBulkOpen(true); setBulkRows({}); }}
                className="ml-2 inline-flex items-center gap-1 rounded-full border border-neutral-300 bg-white hover:bg-neutral-50 px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold text-neutral-800"
                title="Update prices or stock across many SKUs at once — waitlist emails auto-fire per SKU."
              >
                <Edit3 className="h-3 w-3" /> Bulk update
              </button>
            )}
          </div>
        )}
      </div>

      {isLoading && (
        <div className="p-8 flex items-center justify-center text-neutral-400 text-sm">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scanning carts…
        </div>
      )}
      {isError && (
        <div className="p-6 text-sm text-rose-600 bg-rose-50">
          Couldn't load wishlist watchers. Try refreshing.
        </div>
      )}
      {!isLoading && !isError && items.length === 0 && (
        <div
          data-testid="wishlist-watchers-empty"
          className="p-8 text-center text-sm text-neutral-500"
        >
          <Package className="h-10 w-10 mx-auto text-neutral-300 mb-2" />
          No shopper has added any of your SKUs to a cart yet.<br />
          <span className="text-neutral-400 text-xs">
            When someone drops a matching URL into their AI Cart™, they'll show up here.
          </span>
        </div>
      )}
      {!isLoading && items.length > 0 && (
        <div className="divide-y divide-neutral-100">
          {items.map(sku => {
            const isOpen = expanded === sku.product_id;
            return (
              <div key={sku.product_id} data-testid={`wishlist-watcher-row-${sku.product_id}`}>
                <div className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-neutral-50 transition-colors">
                  <button
                    onClick={() => setExpanded(isOpen ? null : sku.product_id)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="text-sm font-semibold text-neutral-900 truncate">
                      {sku.title}
                    </div>
                    <div className="text-[11px] text-neutral-500 flex items-center gap-2 mt-0.5">
                      <span className="inline-flex items-center gap-1">
                        <Users className="h-3 w-3" /> {sku.unique_shoppers} watcher{sku.unique_shoppers === 1 ? "" : "s"}
                      </span>
                      {sku.shared_carts > 0 && (
                        <span>· {sku.shared_carts} shared cart{sku.shared_carts === 1 ? "" : "s"}</span>
                      )}
                      {sku.private_carts > 0 && (
                        <span>· {sku.private_carts} private</span>
                      )}
                      {typeof sku.sibascore === "number" && (
                        <span className="ml-auto rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-mono">
                          SibaScore {sku.sibascore}
                        </span>
                      )}
                    </div>
                  </button>
                  <button
                    data-testid={`wishlist-inventory-open-${sku.product_id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (inventoryEditor === sku.product_id) {
                        setInventoryEditor(null);
                      } else {
                        setInventoryEditor(sku.product_id);
                        setInvPrice("");
                        setInvStock("");
                      }
                    }}
                    className="inline-flex items-center gap-1 rounded-full border border-neutral-300 text-neutral-700 hover:bg-neutral-100 px-2 py-1 text-[10px] uppercase tracking-widest font-bold"
                    title="Update price or stock — auto-fires waitlist emails on drops / restocks."
                  >
                    <Edit3 className="h-3 w-3" /> Edit
                  </button>
                  <button
                    data-testid={`wishlist-notify-open-${sku.product_id}`}
                    onClick={(e) => { e.stopPropagation(); setNotifyTarget(sku); setNotifyEvent("price_drop"); }}
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-2 py-1 text-[10px] uppercase tracking-widest font-bold"
                    title="Email every watcher — price drop, back in stock, or a custom note."
                  >
                    <Mail className="h-3 w-3" /> Notify
                  </button>
                  <button
                    onClick={() => setExpanded(isOpen ? null : sku.product_id)}
                    className="p-1"
                  >
                    <ChevronDown
                      className={`h-4 w-4 text-neutral-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                </div>
                <AnimatePresence initial={false}>
                  {inventoryEditor === sku.product_id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      className="overflow-hidden"
                      data-testid={`wishlist-inventory-editor-${sku.product_id}`}
                    >
                      <div className="px-5 py-3 border-t border-neutral-100 bg-gradient-to-r from-neutral-50 to-white">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-2 flex items-center gap-1">
                          <Edit3 className="h-3 w-3" /> Update inventory
                          <span className="ml-1 normal-case tracking-normal text-neutral-400">
                            · dropping the price or flipping stock to “in stock” auto-emails every watcher
                          </span>
                        </div>
                        <div className="flex flex-wrap items-end gap-3">
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1">
                              New price ({(sku.currency || "INR").toUpperCase()})
                            </div>
                            <div className="relative">
                              <IndianRupee className="h-3.5 w-3.5 text-neutral-400 absolute left-2 top-1/2 -translate-y-1/2" />
                              <input
                                type="number"
                                step="0.01"
                                min={0}
                                data-testid={`wishlist-inventory-price-${sku.product_id}`}
                                value={invPrice}
                                onChange={(e) => setInvPrice(e.target.value)}
                                placeholder={typeof sku.price === "number" ? String(sku.price) : "e.g. 799"}
                                className="w-32 pl-7 pr-2 py-1.5 rounded-lg text-xs border border-neutral-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none font-mono"
                              />
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1">
                              Stock status
                            </div>
                            <select
                              data-testid={`wishlist-inventory-stock-${sku.product_id}`}
                              value={invStock}
                              onChange={(e) => setInvStock(e.target.value)}
                              className="text-xs px-2 py-1.5 rounded-lg border border-neutral-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none bg-white"
                            >
                              <option value="">— unchanged —</option>
                              <option value="in_stock">In stock</option>
                              <option value="out_of_stock">Out of stock</option>
                              <option value="preorder">Preorder</option>
                              <option value="discontinued">Discontinued</option>
                            </select>
                          </div>
                          <div className="ml-auto flex items-center gap-2">
                            <button
                              onClick={() => setInventoryEditor(null)}
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-neutral-700 hover:bg-neutral-100"
                            >
                              Cancel
                            </button>
                            <button
                              data-testid={`wishlist-inventory-save-${sku.product_id}`}
                              onClick={() => submitInventory(sku)}
                              disabled={invSaving}
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-neutral-900 hover:bg-black disabled:opacity-50 inline-flex items-center gap-1"
                            >
                              {invSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                              Save + auto-notify
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.18 }}
                      className="overflow-hidden bg-neutral-50/60"
                    >
                      <div className="px-5 py-3">
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-2 flex items-center gap-1">
                          <Eye className="h-3 w-3" /> Recent watchers
                        </div>
                        {sku.recent_watchers.length === 0 ? (
                          <div className="text-[11px] text-neutral-400 italic">No named watchers yet.</div>
                        ) : (
                          <div className="space-y-1.5">
                            {sku.recent_watchers.map((w, i) => {
                              const nudgedRecently = w.last_nudged_at
                                ? (Date.now() - new Date(w.last_nudged_at).getTime()) < 24 * 3600 * 1000
                                : false;
                              const canNudge = !!w.user_id && !!w.has_phone && !!w.raw_name && !nudgedRecently;
                              const isOpenComposer =
                                nudgeTarget?.productId === sku.product_id &&
                                nudgeTarget?.watcher.user_id === w.user_id;
                              return (
                                <div key={i}>
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span
                                      data-testid={`wishlist-watcher-chip-${sku.product_id}-${i}`}
                                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                                        w.source === "shared"
                                          ? "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200"
                                          : "bg-sky-50 text-sky-800 border-sky-200"
                                      }`}
                                      title={`${w.source} cart · ${fmtWhen(w.at)}`}
                                    >
                                      <span className="truncate max-w-[110px]">{w.name}</span>
                                      <span className="text-[10px] opacity-70">· {fmtWhen(w.at)}</span>
                                    </span>
                                    {canNudge && !isOpenComposer && (
                                      <button
                                        data-testid={`watcher-nudge-open-${sku.product_id}-${i}`}
                                        onClick={() => setNudgeTarget({
                                          productId: sku.product_id,
                                          productTitle: sku.title,
                                          watcher: w,
                                        })}
                                        className="inline-flex items-center gap-1 rounded-full border border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold"
                                      >
                                        <Send className="h-2.5 w-2.5" /> Send nudge
                                      </button>
                                    )}
                                    {nudgedRecently && w.last_nudged_at && (
                                      <span
                                        data-testid={`watcher-nudge-cooldown-${sku.product_id}-${i}`}
                                        className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold"
                                        title="Cooldown: don't ping the same shopper twice in a day. This chip disappears 24h after your last nudge."
                                      >
                                        ⏱ Nudged {fmtWhen(w.last_nudged_at)}
                                      </span>
                                    )}
                                    {!canNudge && !nudgedRecently && w.raw_name && (
                                      <span
                                        className="text-[10px] text-neutral-400 italic"
                                        title="This shopper hasn't added a phone number yet."
                                      >
                                        no phone on file
                                      </span>
                                    )}
                                  </div>
                                  <AnimatePresence>
                                    {isOpenComposer && (
                                      <NudgeComposer
                                        target={nudgeTarget!}
                                        onClose={() => setNudgeTarget(null)}
                                      />
                                    )}
                                  </AnimatePresence>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}
      <AnimatePresence>
        {notifyTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            onClick={() => setNotifyTarget(null)}
            data-testid="wishlist-notify-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-2xl bg-white shadow-2xl p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-bold">
                    Notify wishlist watchers
                  </div>
                  <div className="text-lg font-bold text-neutral-900 mt-1">
                    {notifyTarget.title}
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">
                    {notifyTarget.unique_shoppers} watcher{notifyTarget.unique_shoppers === 1 ? "" : "s"} · one HTML email each.
                  </div>
                </div>
                <button
                  onClick={() => setNotifyTarget(null)}
                  className="text-neutral-400 hover:text-neutral-900"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1.5">
                  What's the news?
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {([
                    { key: "price_drop", label: "💸 Price drop" },
                    { key: "back_in_stock", label: "🎯 Back in stock" },
                    { key: "custom", label: "📣 Custom" },
                  ] as const).map(opt => (
                    <button
                      key={opt.key}
                      data-testid={`wishlist-notify-event-${opt.key}`}
                      onClick={() => setNotifyEvent(opt.key)}
                      aria-pressed={notifyEvent === opt.key}
                      className={`text-xs font-semibold px-2 py-2 rounded-lg border transition-colors ${
                        notifyEvent === opt.key
                          ? "bg-emerald-500 text-white border-emerald-500"
                          : "bg-white text-neutral-700 border-neutral-200 hover:border-emerald-300"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {notifyEvent === "price_drop" && (
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1">
                    New price ({notifyTarget.currency || "INR"})
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    value={notifyPrice}
                    data-testid="wishlist-notify-price"
                    onChange={(e) => setNotifyPrice(e.target.value)}
                    placeholder="e.g. 799"
                    className="w-full text-sm px-3 py-2 rounded-lg border border-neutral-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none"
                  />
                </div>
              )}
              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1">
                  Optional personal note
                </div>
                <textarea
                  data-testid="wishlist-notify-message"
                  value={notifyMessage}
                  onChange={(e) => setNotifyMessage(e.target.value)}
                  rows={3}
                  placeholder="A short line from you — visible under the price/badge in the email."
                  className="w-full text-xs px-2.5 py-2 rounded-lg border border-neutral-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none resize-none"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setNotifyTarget(null)}
                  className="px-3 py-2 rounded-lg text-sm font-semibold text-neutral-700 hover:bg-neutral-100"
                >
                  Cancel
                </button>
                <button
                  data-testid="wishlist-notify-send"
                  onClick={submitNotify}
                  disabled={notifySending || (notifyEvent === "price_drop" && !notifyPrice)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  {notifySending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  Send emails
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {bulkOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            onClick={() => setBulkOpen(false)}
            data-testid="wishlist-bulk-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl p-6 max-h-[85vh] overflow-hidden flex flex-col"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-bold">
                    Bulk inventory update
                  </div>
                  <div className="text-lg font-bold text-neutral-900 mt-0.5">
                    Drop prices or flip stock across many SKUs
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">
                    Each edited row auto-fires a waitlist email per SKU. Leave a field blank to skip it.
                  </div>
                </div>
                <button onClick={() => setBulkOpen(false)} className="text-neutral-400 hover:text-neutral-900">
                  <XIcon className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 overflow-auto border border-neutral-200 rounded-xl">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 border-b border-neutral-200 sticky top-0">
                    <tr className="text-[10px] uppercase tracking-widest text-neutral-600">
                      <th className="text-left px-3 py-2 font-bold">SKU</th>
                      <th className="text-right px-3 py-2 font-bold">Watchers</th>
                      <th className="text-left px-3 py-2 font-bold">New price</th>
                      <th className="text-left px-3 py-2 font-bold">Stock status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {(data?.items || []).map((sku) => {
                      const row = bulkRows[sku.product_id] || { price: "", stock: "" };
                      const setRow = (patch: Partial<typeof row>) =>
                        setBulkRows(prev => ({ ...prev, [sku.product_id]: { ...row, ...patch } }));
                      return (
                        <tr key={sku.product_id} data-testid={`wishlist-bulk-row-${sku.product_id}`}>
                          <td className="px-3 py-2">
                            <div className="text-xs font-semibold text-neutral-900 truncate max-w-[220px]">{sku.title}</div>
                            {typeof sku.price === "number" && (
                              <div className="text-[10px] text-neutral-400 font-mono">now {(sku.currency || "INR")} {sku.price}</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-xs text-neutral-600 tabular-nums">
                            {sku.unique_shoppers}
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              step="0.01"
                              min={0}
                              data-testid={`wishlist-bulk-price-${sku.product_id}`}
                              value={row.price}
                              onChange={(e) => setRow({ price: e.target.value })}
                              placeholder={typeof sku.price === "number" ? String(sku.price) : "e.g. 799"}
                              className="w-28 px-2 py-1 rounded-lg text-xs border border-neutral-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none font-mono"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <select
                              data-testid={`wishlist-bulk-stock-${sku.product_id}`}
                              value={row.stock}
                              onChange={(e) => setRow({ stock: e.target.value })}
                              className="text-xs px-2 py-1 rounded-lg border border-neutral-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15 outline-none bg-white"
                            >
                              <option value="">— unchanged —</option>
                              <option value="in_stock">In stock</option>
                              <option value="out_of_stock">Out of stock</option>
                              <option value="preorder">Preorder</option>
                              <option value="discontinued">Discontinued</option>
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between mt-4 gap-3">
                <div className="text-[11px] text-neutral-500">
                  Rows with any edit: <span className="font-bold text-neutral-800">
                    {Object.values(bulkRows).filter(r => r.price.trim() || r.stock.trim()).length}
                  </span> / {data?.items?.length || 0}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setBulkOpen(false)}
                    className="px-3 py-2 rounded-lg text-sm font-semibold text-neutral-700 hover:bg-neutral-100"
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="wishlist-bulk-save"
                    onClick={submitBulk}
                    disabled={bulkSaving}
                    className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-neutral-900 hover:bg-black disabled:opacity-50 inline-flex items-center gap-1.5"
                  >
                    {bulkSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Save all + auto-notify
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WishlistWatchersCard;
